import { AbstractApiStore, AbstractEntityState } from '@/store/abstract-api-store';
import { Action, Module, Mutation } from 'vuex-module-decorators';
import store from '@/store';
import { StoreModuleTypes } from '@/constants/store-constants';
import { TextMessage } from '@/communications/messages/models/text';
import { TextsRepository } from '@/communications/messages/repositories/texts-repository';
import { MessagesStore } from '@/communications/messages/stores/messages-store';
import { InboxCountParams } from '@/communications/messages/models/inbox-params';

export interface TextsState extends AbstractEntityState<TextMessage> {
    entities: Array<TextMessage>;
    currentFamilyId: number|undefined;
    orgInboxCount: number;
    currentOrgId: number|undefined;
}

@Module({
    namespaced: true,
    dynamic: true,
    store,
    name: StoreModuleTypes.TEXTS
})
export class TextsStore extends AbstractApiStore<TextMessage> implements TextsState, MessagesStore {
    protected readonly repository = new TextsRepository();

    entities: Array<TextMessage> = [];
    currentFamilyId: number|undefined = undefined;
    orgInboxCount = 0;
    teamInboxCount = 0;
    currentOrgId: number|undefined = undefined;
    private isTeamCountInit = false;

    @Mutation
    private storeTextsForFamily(update: { texts: Array<TextMessage>; familyId: number }) {
        this.entities = update.texts;
        this.currentFamilyId = update.familyId;
    }

    @Action({ commit: 'storeTextsForFamily' })
    public async retrieveTextsForFamily(familyId: number) {
        const texts = await this.repository.getMessagesForFamily(familyId);
        return { texts: texts.entities, familyId };
    }

    @Action
    public async initTexts(familyId: number) {
        if (this.currentFamilyId !== familyId) {
            await this.retrieveTextsForFamily(familyId);
        }
    }

    @Mutation
    private storeTextsInboxCount(update: { count: number; orgId: number }) {
        this.orgInboxCount = update.count;
        this.currentOrgId = update.orgId;
    }

    @Mutation
    private storeTextsInboxCountForEnrollmentTeam(count: number) {
        this.teamInboxCount = count;
        this.isTeamCountInit = true;
    }

    @Mutation
    public incrementInboxCount() {
        this.orgInboxCount++;
    }

    @Mutation
    public decrementInboxCount() {
        this.orgInboxCount--;
    }

    @Mutation
    public incrementTeamInboxCount() {
        this.teamInboxCount++;
    }

    @Mutation
    public decrementTeamInboxCount() {
        this.teamInboxCount--;
    }

    @Action({ commit: 'storeTextsInboxCount' })
    public async retrieveTextsInboxCount(params: InboxCountParams) {
        const count = await this.repository.getInboxCount(params);
        return { count, orgId: params.org_id };
    }

    @Action({ commit: 'storeTextsInboxCountForEnrollmentTeam' })
    public async retrieveTextsInboxCountForEnrollmentTeam(params: InboxCountParams) {
        params.only_enrollment_team = true;
        return await this.repository.getInboxCount(params);
    }

    @Action
    public async initTextsInboxCount(params: InboxCountParams) {
        if (this.currentOrgId !== params.org_id) {
            await this.retrieveTextsInboxCount(params);
        }
    }

    @Action
    public async initTextsInboxCountForEnrollmentTeam(params: InboxCountParams) {
        if (!this.isTeamCountInit) {
            await this.retrieveTextsInboxCountForEnrollmentTeam(params);
        }
    }

    // unfortunately getters don't work with vuex-module-decorator inheritance for some reason
    public get stored(): Array<TextMessage> {
        return this.entities;
    }

    public get inboxCount(): number {
        return this.orgInboxCount;
    }

    public get enrollmentTeamInboxCount(): number {
        return this.teamInboxCount;
    }
}
